import { useState, useEffect } from 'react';
import PageHeader from '../components/PageHeader';
import PartCard from '../components/PartCard';
import { supabase } from '../lib/supabase';
import { API_BASE } from '../apiBase';

const colors = {
  bg: '#f0f1f3',
  cardBg: '#ffffff',
  navy: '#071a52',
  navyLight: '#0b2981',
  greenDark: '#5cb83c',
  green: '#a7ff83',
  textPrimary: '#1a1a2e',
  textSecondary: '#6b7280',
  border: '#e2e4e8',
};

const PROXY = API_BASE;

const zones = [
  { key: 'bin', label: 'Bins' },
  { key: 'tub', label: 'Tubs' },
  { key: 'tray', label: 'Trays' },
  { key: 'wd', label: 'White Drawers' },
  { key: 'mf', label: 'Minifigs' },
  { key: 'binder', label: 'Binder' },
  { key: 'other', label: 'Other' },
];

// Classify a bin name into a zone by its shape. Any letter-pair-then-digits
// bin (AA001, BB001, and any future series) lands in "Bins"; anything that
// doesn't match a known shape lands in "Other" so nothing is ever orphaned.
function binZoneOf(name) {
  const u = (name || '').trim().toUpperCase();
  if (u.startsWith('TUB')) return 'tub';
  if (u.startsWith('TRAY')) return 'tray';
  if (u.startsWith('WD')) return 'wd';
  if (u.startsWith('MF')) return 'mf';
  if (u.startsWith('BINDER')) return 'binder';
  if (/^[A-Z]{2}\d/.test(u)) return 'bin';
  return 'other';
}

// Natural sort so AA2 < AA10 and "TUB 2" < "TUB 10".
function binCompare(a, b) {
  return String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: 'base' });
}

// The real bins are whatever the live inventory actually uses. Derive the
// distinct bin names straight from remarks — no hardcoded ranges — so a new
// series (BB…) is auditable the moment a lot is placed there.
function discoverBins(inventory) {
  const set = new Set();
  (inventory || []).forEach(l => {
    const name = (l.remarks || '').trim() || '(no remarks)';
    set.add(name);
  });
  return Array.from(set).sort(binCompare);
}

// Inline correction panel shown when a part is flagged
function CorrectionPanel({ lot, onUnflag, onPushed }) {
  const [qty, setQty] = useState(String(lot.quantity ?? ''));
  const [location, setLocation] = useState(lot.remarks ?? '');
  const [pushing, setPushing] = useState(false);
  const [pushed, setPushed] = useState(false);
  const [error, setError] = useState(null);

  async function handlePush() {
    setPushing(true);
    setError(null);
    try {
      const body = {};
      const newQty = parseInt(qty, 10);
      if (!isNaN(newQty) && newQty !== lot.quantity) body.quantity = newQty - lot.quantity;
      if (location.trim() !== (lot.remarks ?? '').trim()) body.remarks = location.trim();

      if (Object.keys(body).length === 0) {
        setError('No changes detected.');
        setPushing(false);
        return;
      }

      const res = await fetch(`${PROXY}/api/inventories/${lot.inventory_id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });

      if (!res.ok) {
        const text = await res.text();
        throw new Error(`BrickLink error: ${text}`);
      }

      setPushed(true);
      onPushed(lot.inventory_id, body);
    } catch (err) {
      setError(err.message);
    } finally {
      setPushing(false);
    }
  }

  if (pushed) {
    return (
      <div style={{
        marginTop: '6px',
        padding: '10px',
        background: '#f0fdf4',
        border: '1px solid #bbf7d0',
        borderRadius: '6px',
        textAlign: 'center',
      }}>
        <div style={{ color: colors.greenDark, fontWeight: 700, fontSize: '0.78rem' }}>✓ Pushed to BrickLink</div>
        <button
          onClick={onUnflag}
          style={{
            marginTop: '6px',
            padding: '4px 12px',
            background: 'none',
            border: `1px solid ${colors.border}`,
            borderRadius: '4px',
            color: colors.textSecondary,
            fontFamily: "'Outfit', sans-serif",
            fontSize: '0.72rem',
            cursor: 'pointer',
          }}
        >
          Dismiss
        </button>
      </div>
    );
  }

  return (
    <div style={{
      marginTop: '6px',
      padding: '10px',
      background: '#fff7ed',
      border: '1px solid #fed7aa',
      borderLeft: '3px solid #ea580c',
      borderRadius: '6px',
    }}>
      <div style={{
        fontSize: '0.68rem',
        fontWeight: 700,
        color: '#ea580c',
        textTransform: 'uppercase',
        letterSpacing: '0.06em',
        marginBottom: '8px',
      }}>⚑ Fix &amp; Push</div>

      {/* Quantity field */}
      <div style={{ marginBottom: '6px' }}>
        <div style={{ fontSize: '0.68rem', color: colors.textSecondary, marginBottom: '3px' }}>Quantity</div>
        <input
          type="number"
          value={qty}
          onChange={e => setQty(e.target.value)}
          style={{
            width: '100%',
            padding: '6px 8px',
            background: '#fff',
            border: `1px solid ${colors.border}`,
            borderRadius: '4px',
            fontFamily: "'Outfit', sans-serif",
            fontSize: '0.85rem',
            color: colors.textPrimary,
            outline: 'none',
            boxSizing: 'border-box',
          }}
        />
      </div>

      {/* Location field */}
      <div style={{ marginBottom: '8px' }}>
        <div style={{ fontSize: '0.68rem', color: colors.textSecondary, marginBottom: '3px' }}>Location (Remarks)</div>
        <input
          type="text"
          value={location}
          onChange={e => setLocation(e.target.value)}
          placeholder="e.g. AA001"
          style={{
            width: '100%',
            padding: '6px 8px',
            background: '#fff',
            border: `1px solid ${colors.border}`,
            borderRadius: '4px',
            fontFamily: "'Outfit', sans-serif",
            fontSize: '0.85rem',
            color: colors.textPrimary,
            outline: 'none',
            boxSizing: 'border-box',
          }}
        />
      </div>

      {error && (
        <div style={{
          fontSize: '0.72rem',
          color: '#dc2626',
          marginBottom: '6px',
          wordBreak: 'break-word',
        }}>{error}</div>
      )}

      <div style={{ display: 'flex', gap: '6px' }}>
        <button
          onClick={handlePush}
          disabled={pushing}
          style={{
            flex: 2,
            padding: '7px',
            background: pushing ? '#d1d5db' : '#ea580c',
            color: '#fff',
            border: 'none',
            borderRadius: '4px',
            fontFamily: "'Outfit', sans-serif",
            fontWeight: 700,
            fontSize: '0.75rem',
            cursor: pushing ? 'default' : 'pointer',
          }}
        >
          {pushing ? 'Pushing...' : 'Push to BrickLink'}
        </button>
        <button
          onClick={onUnflag}
          style={{
            flex: 1,
            padding: '7px',
            background: '#fff',
            border: `1px solid ${colors.border}`,
            borderRadius: '4px',
            color: colors.textSecondary,
            fontFamily: "'Outfit', sans-serif",
            fontSize: '0.75rem',
            cursor: 'pointer',
          }}
        >
          Cancel
        </button>
      </div>
    </div>
  );
}

function formatAuditDate(dateStr) {
  if (!dateStr) return null;
  const d = new Date(dateStr);
  const now = new Date();
  const diff = Math.floor((now - d) / (1000 * 60 * 60 * 24));
  if (diff === 0) return 'Today';
  if (diff === 1) return '1d ago';
  return `${diff}d ago`;
}
function Audit() {
  const [inventory, setInventory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [auditMode, setAuditMode] = useState(false);
  const [zone, setZone] = useState('bin');
  const [selectedPrefix, setSelectedPrefix] = useState('');
  const [binQueue, setBinQueue] = useState([]);
  const [binIndex, setBinIndex] = useState(0);
  const [checked, setChecked] = useState(new Set());
  const [flagged, setFlagged] = useState(new Set());
  const [skipped, setSkipped] = useState(new Set());
  const [search, setSearch] = useState('');
  const [moving, setMoving] = useState(null); // inventory_id of part being moved
  const [moveBin, setMoveBin] = useState('');
  const [movePushing, setMovePushing] = useState(false);
  const [prices, setPrices] = useState({});
  const [pricesLoading, setPricesLoading] = useState(false);
  const [auditDates, setAuditDates] = useState({});

  // Keyboard shortcuts
  useEffect(() => {
    function handleKeyDown(e) {
      if (e.code !== 'Space') return;
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'SELECT') return;
      e.preventDefault();
      if (!auditMode) return;
      if (binIndex < binQueue.length) {
        const bin = binQueue[binIndex];
        const lots = inventory.filter(l => (l.remarks || '(no remarks)') === bin);
        const done = lots.filter(l =>
          checked.has(l.inventory_id) ||
          flagged.has(l.inventory_id) ||
          skipped.has(l.inventory_id)
        ).length;
        if (lots.length > 0 && done === lots.length) {
          markBinAudited(binQueue[binIndex]);
          setBinIndex(prev => prev + 1);
          setSearch('');
          return;
        }
        const visible = lots
          .filter(l => !checked.has(l.inventory_id) && !skipped.has(l.inventory_id))
          .sort((a, b) => (a.item?.no || '').localeCompare(b.item?.no || ''));
        if (visible.length > 0) {
          setChecked(prev => new Set([...prev, visible[0].inventory_id]));
        }
      }
    }
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [auditMode, binIndex, binQueue, inventory, checked, flagged, skipped]);

  useEffect(() => {
    fetch(`${PROXY}/api/inventories`)
      .then(res => res.json())
      .then(data => {
        setInventory(data.data || []);
        setLoading(false);
      })
      .catch(err => {
        console.error(err);
        setLoading(false);
      });
  }, []);

  useEffect(() => {
    supabase.from('bin_audit_dates').select('bin_name, last_audited')
      .then(({ data }) => {
        if (data) {
          const map = {};
          data.forEach(row => { map[row.bin_name] = row.last_audited; });
          setAuditDates(map);
        }
      });
  }, []);
useEffect(() => {
    if (!auditMode || binIndex >= binQueue.length) return;
    const currentBin = binQueue[binIndex];
    const lots = inventory.filter(l => (l.remarks || '(no remarks)') === currentBin);
    if (lots.length === 0) return;

    setPricesLoading(true);
    const newPrices = {};

    async function fetchPrices() {
      for (let i = 0; i < lots.length; i++) {
        const lot = lots[i];
        const pno = lot.item?.no;
        const cid = lot.color_id;
        const type = (lot.remarks || '').toUpperCase().startsWith('MF') ? 'minifig' : 'part';
        if (!pno) continue;
        const key = lot.inventory_id;
        try {
          const res = await fetch(`${PROXY}/api/price/${type}/${pno}?color_id=${cid}&guide_type=sold`);
          const json = await res.json();
          const avg = parseFloat(json.data?.avg_price || 0);
          const myPrice = parseFloat(lot.unit_price || 0);
          if (avg > 0 && myPrice > 0) {
            const ratio = myPrice / avg;
            if (ratio < 0.7) newPrices[key] = { avg, status: 'LOW' };
            else if (ratio > 1.5) newPrices[key] = { avg, status: 'HIGH' };
            else newPrices[key] = { avg, status: 'OK' };
          }
        } catch (e) { /* skip */ }
        // Small delay to avoid rate limiting
        if (i < lots.length - 1) await new Promise(r => setTimeout(r, 200));
      }
      setPrices(prev => ({ ...prev, ...newPrices }));
      setPricesLoading(false);
    }
    fetchPrices();
  }, [auditMode, binIndex, binQueue, inventory]);

  function launchFrom(queue, startName) {
    const startIdx = startName ? queue.indexOf(startName) : 0;
    const finalQueue = startIdx >= 0 ? queue.slice(startIdx) : queue;
    setBinQueue(finalQueue);
    setBinIndex(0);
    setSearch('');
    setAuditMode(true);
  }

  async function handleMove(lid) {
    if (!moveBin.trim()) return;
    setMovePushing(true);
    const lot = inventory.find(l => l.inventory_id === lid);
    const oldBin = lot?.remarks || '';
    const newBin = moveBin.trim().toUpperCase();
    try {
      const res = await fetch(`${PROXY}/api/inventories/${lid}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ remarks: newBin }),
      });
      if (!res.ok) throw new Error(await res.text());
      await supabase.from('storage_history').insert({
        inventory_id: lid,
        part_no: lot?.item?.no || '',
        color_name: lot?.color_name || '',
        from_bin: oldBin,
        to_bin: newBin,
        moved_at: new Date().toISOString(),
      });
      setInventory(prev => prev.map(l => l.inventory_id === lid ? { ...l, remarks: newBin } : l));
      setChecked(prev => new Set([...prev, lid]));
      setMoving(null);
      setMoveBin('');
    } catch (err) {
      console.error('Move error:', err);
    }
    setMovePushing(false);
  }
  const handleFound = (lid) => setChecked(prev => new Set([...prev, lid]));
  const handleSkip = (lid) => setSkipped(prev => new Set([...prev, lid]));
  const handleFlag = (lid) => setFlagged(prev => new Set([...prev, lid]));
  const handleUnflag = (lid) => setFlagged(prev => { const n = new Set(prev); n.delete(lid); return n; });
  async function markBinAudited(binName) {
    const now = new Date().toISOString();
    setAuditDates(prev => ({ ...prev, [binName]: now }));
    await supabase.from('bin_audit_dates').upsert(
      { bin_name: binName, last_audited: now },
      { onConflict: 'bin_name' }
    );
  }

  // When a push succeeds, mark as checked and update local inventory
  function handlePushed(lid, changes) {
    setInventory(prev => prev.map(l => {
      if (l.inventory_id !== lid) return l;
      return {
        ...l,
        ...(changes.quantity !== undefined ? { quantity: changes.quantity } : {}),
        ...(changes.remarks !== undefined ? { remarks: changes.remarks } : {}),
      };
    }));
    setChecked(prev => new Set([...prev, lid]));
    setFlagged(prev => { const n = new Set(prev); n.delete(lid); return n; });
  }

  if (loading) {
    return (
      <div style={{
        minHeight: '100vh',
        background: colors.bg,
        padding: '20px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
      }}>
        <div style={{ fontSize: '1.8rem', marginBottom: '12px', color: colors.navy }}>⟳</div>
        <div style={{ fontSize: '1.2rem', fontWeight: 700, color: colors.textPrimary }}>
          Loading inventory from BrickLink...
        </div>
      </div>
    );
  }

  // Setup screen
  if (!auditMode) {
    const allBins = discoverBins(inventory);
    const zoneCounts = {};
    zones.forEach(z => { zoneCounts[z.key] = 0; });
    allBins.forEach(b => { const zk = binZoneOf(b); zoneCounts[zk] = (zoneCounts[zk] || 0) + 1; });
    const zoneBins = allBins.filter(b => binZoneOf(b) === zone);
    // Distinct 2-letter row prefixes present in the "bin" zone (AA, BB, …).
    const binPrefixes = Array.from(new Set(
      allBins.filter(b => binZoneOf(b) === 'bin').map(b => b.trim().toUpperCase().slice(0, 2))
    )).sort(binCompare);
    const prefixBins = selectedPrefix
      ? zoneBins.filter(name => name.trim().toUpperCase().startsWith(selectedPrefix))
      : [];

    return (
      <div style={{
        minHeight: '100vh',
        background: colors.bg,
        padding: '20px',
        fontFamily: "'Outfit', sans-serif",
      }}>
        <PageHeader title="Audit Mode" icon="✓" />

        {/* Stats */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(4, 1fr)',
          gap: '10px',
          marginBottom: '20px',
        }}>
          {(() => {
            const auditedBins = new Set(Object.keys(auditDates));
            const auditedLots = inventory.filter(l => auditedBins.has(l.remarks || '')).length;
            const unauditedLots = inventory.length - auditedLots;
            return [
              { value: `${auditedLots}`, label: 'Audited Lots', accent: colors.greenDark },
              { value: `${unauditedLots}`, label: 'Remaining', accent: '#ea580c' },
              { value: `${auditedBins.size}`, label: 'Bins Done', accent: '#7c3aed' },
              { value: `${inventory.length}`, label: 'Total Lots', accent: colors.navy },
            ];
          })().map((stat, i) => (

            <div key={i} style={{
              background: colors.cardBg,
              border: `1px solid ${colors.border}`,
              borderBottom: `3px solid ${stat.accent}`,
              borderRadius: '6px',
              padding: '14px',
              textAlign: 'center',
            }}>
              <div style={{ fontSize: '1.6rem', fontWeight: 800, color: stat.accent }}>{stat.value}</div>
              <div style={{ fontSize: '0.68rem', color: colors.textSecondary, textTransform: 'uppercase', letterSpacing: '0.06em', marginTop: '4px' }}>{stat.label}</div>
            </div>
          ))}
        </div>

        {/* Zone Tabs */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(3, 1fr)',
          gap: '10px',
          marginBottom: '16px',
        }}>
          {zones.map(z => {
            const isActive = zone === z.key;
            return (
              <div
                key={z.key}
                onClick={() => { setZone(z.key); setSelectedPrefix(''); }}
                style={{
                  background: isActive ? colors.navy : colors.cardBg,
                  border: `1px solid ${isActive ? colors.navy : colors.border}`,
                  borderRadius: '6px',
                  padding: '14px 10px',
                  textAlign: 'center',
                  cursor: 'pointer',
                }}
              >
                <div style={{
                  fontSize: '1rem',
                  fontWeight: 700,
                  color: isActive ? '#ffffff' : colors.textPrimary,
                }}>{z.label}</div>
                <div style={{
                  fontSize: '0.68rem',
                  color: isActive ? '#94a3b8' : colors.textSecondary,
                  marginTop: '3px',
                }}>{zoneCounts[z.key] || 0} bin{zoneCounts[z.key] === 1 ? '' : 's'}</div>
              </div>
            );
          })}
        </div>

        {/* Zone label */}
        {(() => {
          const bins = selectedPrefix ? prefixBins : zoneBins;
          const audited = bins.filter(b => auditDates[b]).length;
          const total = bins.length;
          const pctDone = total ? Math.round(audited / total * 100) : 0;
          return (
            <>
              <div style={{
                fontSize: '0.78rem',
                color: colors.textSecondary,
                marginBottom: '8px',
                textTransform: 'uppercase',
                letterSpacing: '0.06em',
              }}>
                {zones.find(z => z.key === zone)?.label}
                {selectedPrefix ? ` · Row ${selectedPrefix} · ${prefixBins.length} bins` : ` · ${zoneBins.length} locations`}
                {' · Tap a bin to start from there'}
              </div>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                marginBottom: '16px',
              }}>
                <div style={{
                  flex: 1,
                  height: '8px',
                  background: '#e2e4e8',
                  borderRadius: '4px',
                  overflow: 'hidden',
                }}>
                  <div style={{
                    width: `${pctDone}%`,
                    height: '100%',
                    background: colors.greenDark,
                    borderRadius: '4px',
                    transition: 'width 0.3s ease',
                  }} />
                </div>
                <div style={{
                  fontSize: '0.72rem',
                  fontWeight: 700,
                  color: colors.greenDark,
                  whiteSpace: 'nowrap',
                }}>
                  {audited}/{total} audited ({pctDone}%)
                </div>
              </div>
            </>
          );
        })()}

        {/* Bin Grid */}
        {zone === 'bin' && !selectedPrefix ? (
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(5, 1fr)',
            gap: '8px',
            marginBottom: '20px',
          }}>
            {binPrefixes.map(prefix => (
              <div
                key={prefix}
                onClick={() => setSelectedPrefix(prefix)}
                style={{
                  background: colors.cardBg,
                  border: `1px solid ${colors.border}`,
                  borderRadius: '6px',
                  padding: '14px 6px',
                  textAlign: 'center',
                  cursor: 'pointer',
                }}
              >
                <div style={{ fontSize: '1rem', fontWeight: 800, color: colors.textPrimary }}>{prefix}</div>
              </div>
            ))}
          </div>

        ) : zone === 'bin' && selectedPrefix ? (
          <>
            <button
              onClick={() => setSelectedPrefix('')}
              style={{
                marginBottom: '12px',
                padding: '8px 16px',
                background: colors.cardBg,
                border: `1px solid ${colors.border}`,
                borderRadius: '6px',
                color: colors.textSecondary,
                fontFamily: "'Outfit', sans-serif",
                fontWeight: 600,
                fontSize: '0.8rem',
                cursor: 'pointer',
              }}
            >
              ← Back to rows
            </button>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(5, 1fr)',
              gap: '8px',
              marginBottom: '20px',
            }}>
              {prefixBins.map(name => {
                const dateLabel = formatAuditDate(auditDates[name]);
                return (
                  <div
                    key={name}
                    onClick={() => launchFrom(prefixBins, name)}
                    style={{
                      background: colors.cardBg,
                      border: `1px solid ${dateLabel ? '#bbf7d0' : colors.border}`,
                      borderRadius: '6px',
                      padding: '12px 6px',
                      textAlign: 'center',
                      cursor: 'pointer',
                    }}
                  >
                    <div style={{ fontSize: '0.85rem', fontWeight: 700, color: colors.textPrimary }}>{name}</div>
                    {dateLabel && (
                      <div style={{ fontSize: '0.58rem', color: colors.greenDark, marginTop: '3px' }}>✓ {dateLabel}</div>
                    )}
                  </div>
                );
              })}
            </div>
          </>

        ) : (
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(5, 1fr)',
            gap: '8px',
            marginBottom: '20px',
          }}>
            {zoneBins.map(name => {
              const dateLabel = formatAuditDate(auditDates[name]);
              return (
                <div
                  key={name}
                  onClick={() => launchFrom(zoneBins, name)}
                  style={{
                    background: colors.cardBg,
                    border: `1px solid ${dateLabel ? '#bbf7d0' : colors.border}`,
                    borderRadius: '6px',
                    padding: '12px 6px',
                    textAlign: 'center',
                    cursor: 'pointer',
                  }}
                >
                  <div style={{ fontSize: '0.85rem', fontWeight: 700, color: colors.textPrimary }}>{name}</div>
                  {dateLabel && (
                    <div style={{ fontSize: '0.58rem', color: colors.greenDark, marginTop: '3px' }}>✓ {dateLabel}</div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        <div
          onClick={() => window.location.href = '/skipped'}
          style={{
            background: colors.cardBg,
            border: `1px solid ${colors.border}`,
            borderRadius: '6px',
            padding: '14px',
            textAlign: 'center',
            cursor: 'pointer',
            marginBottom: '12px',
          }}
        >
          <div style={{ fontSize: '1.2rem', marginBottom: '4px', color: '#7c3aed' }}>◎</div>
          <div style={{ fontSize: '0.82rem', fontWeight: 700, color: colors.textPrimary }}>Skipped Items</div>
          <div style={{ fontSize: '0.65rem', color: colors.textSecondary }}>Review parts skipped during audits</div>
        </div>
        <button
          onClick={() => {
            const queue = selectedPrefix ? prefixBins : zoneBins;
            launchFrom(queue, null);
          }}
          style={{
            width: '100%',
            padding: '16px 24px',
            background: colors.navy,
            color: '#ffffff',
            border: 'none',
            borderRadius: '6px',
            fontSize: '1rem',
            fontWeight: 800,
            fontFamily: "'Outfit', sans-serif",
            cursor: 'pointer',
          }}
        >
          {selectedPrefix
            ? `Start ${selectedPrefix} from Beginning`
            : `Start ${zones.find(z => z.key === zone)?.label} from Beginning`}
        </button>
      </div>
    );
  }

  // Audit complete
  if (binIndex >= binQueue.length) {
    return (
      <div style={{
        minHeight: '100vh',
        background: colors.bg,
        padding: '20px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        fontFamily: "'Outfit', sans-serif",
      }}>
        <div style={{ fontSize: '2.5rem', marginBottom: '12px', color: colors.greenDark }}>✓</div>
        <div style={{ fontSize: '2rem', fontWeight: 800, color: colors.greenDark, marginBottom: '8px' }}>
          Audit Complete!
        </div>
        <div style={{ fontSize: '1rem', color: colors.textSecondary, marginBottom: '24px', textAlign: 'center' }}>
          All {binQueue.length} bins audited · {checked.size} found · {flagged.size} flagged · {skipped.size} skipped
        </div>
        <button
          onClick={() => setAuditMode(false)}
          style={{
            padding: '14px 32px',
            background: colors.navy,
            color: '#ffffff',
            border: 'none',
            borderRadius: '6px',
            fontSize: '1rem',
            fontWeight: 800,
            fontFamily: "'Outfit', sans-serif",
            cursor: 'pointer',
          }}
        >
          Back to Audit Setup
        </button>
      </div>
    );
  }

  const currentBin = binQueue[binIndex];
  const currentLots = inventory
    .filter(l => (l.remarks || '(no remarks)') === currentBin)
    .sort((a, b) => (a.item?.no || '').localeCompare(b.item?.no || '') || (a.quantity || 0) - (b.quantity || 0));

  const doneCount = currentLots.filter(l =>
    checked.has(l.inventory_id) ||
    flagged.has(l.inventory_id) ||
    skipped.has(l.inventory_id)
  ).length;
  const pct = currentLots.length ? Math.round(doneCount / currentLots.length * 100) : 0;
  const allDone = currentLots.length > 0 && doneCount === currentLots.length;

  let visibleLots = currentLots.filter(l =>
    !checked.has(l.inventory_id) &&
    !skipped.has(l.inventory_id)
  );
  if (search) {
    const q = search.toLowerCase();
    const matches = visibleLots.filter(l => (l.item?.no || '').toLowerCase().includes(q));
    const nonMatches = visibleLots.filter(l => !(l.item?.no || '').toLowerCase().includes(q));
    visibleLots = [...matches, ...nonMatches];
  }

  return (
    <div style={{
      minHeight: '100vh',
      background: colors.bg,
      padding: '20px',
      fontFamily: "'Outfit', sans-serif",
    }}>
      {/* Audit header */}
      <div style={{
        background: colors.navy,
        borderLeft: `4px solid ${colors.green}`,
        borderRadius: '8px',
        padding: '20px 24px',
        marginBottom: '20px',
      }}>
        <div style={{
          fontSize: '0.68rem',
          fontWeight: 700,
          color: colors.green,
          textTransform: 'uppercase',
          letterSpacing: '0.1em',
          marginBottom: '6px',
        }}>✓ Audit Mode · Bin {binIndex + 1} of {binQueue.length}</div>
        <div style={{
          fontSize: '1.5rem',
          fontWeight: 800,
          color: '#ffffff',
          marginBottom: '12px',
        }}>{currentBin}</div>
        <div style={{
          width: '100%',
          height: '6px',
          background: 'rgba(255,255,255,0.15)',
          borderRadius: '3px',
          overflow: 'hidden',
          marginBottom: '8px',
        }}>
          <div style={{
            width: `${pct}%`,
            height: '100%',
            background: colors.green,
            borderRadius: '3px',
            transition: 'width 0.3s ease',
          }} />
        </div>
        <div style={{ fontSize: '0.75rem', color: '#94a3b8' }}>
          {doneCount}/{currentLots.length} lots done · {pct}%
        </div>
      </div>

      {/* Bin complete banner */}
      {allDone && (
        <div style={{
          background: '#f0fdf4',
          border: '2px solid #bbf7d0',
          borderRadius: '8px',
          padding: '24px',
          textAlign: 'center',
          marginBottom: '20px',
        }}>
          <div style={{ fontSize: '1.8rem', marginBottom: '8px', color: colors.greenDark }}>✓</div>
          <div style={{ fontSize: '1.2rem', fontWeight: 800, color: colors.greenDark, marginBottom: '12px' }}>
            {currentBin} complete!
          </div>
          <button
            onClick={() => { markBinAudited(currentBin); setBinIndex(prev => prev + 1); setSearch(''); }}
            style={{
              padding: '12px 28px',
              background: colors.navy,
              color: '#ffffff',
              border: 'none',
              borderRadius: '6px',
              fontSize: '0.9rem',
              fontWeight: 700,
              fontFamily: "'Outfit', sans-serif",
              cursor: 'pointer',
            }}
          >
            Continue to next bin →
          </button>
        </div>
      )}

      {/* Scan bar */}
      {!allDone && (
        <div style={{ display: 'flex', gap: '8px', marginBottom: '16px' }}>
          <input
            type="text"
            placeholder="Scan or search part #..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{
              flex: 1,
              padding: '12px 16px',
              background: colors.cardBg,
              border: `1px solid ${colors.border}`,
              borderRadius: '6px',
              color: colors.textPrimary,
              fontFamily: "'Outfit', sans-serif",
              fontSize: '0.9rem',
              outline: 'none',
            }}
          />
          <button
            onClick={() => setSearch('')}
            style={{
              padding: '12px 16px',
              background: colors.cardBg,
              border: `1px solid ${colors.border}`,
              borderRadius: '6px',
              color: colors.textSecondary,
              fontFamily: "'Outfit', sans-serif",
              fontSize: '0.85rem',
              cursor: 'pointer',
            }}
          >
            Clear
          </button>
        </div>
      )}

      {/* Part cards */}
      {!allDone && (
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))',
          gap: '10px',
        }}>
          {visibleLots.map(lot => {
            const lid = lot.inventory_id;
            const isFlagged = flagged.has(lid);
            return (
              <div key={lid}>
                <PartCard lot={lot} />
                {prices[lid] && prices[lid].status !== 'OK' && (
                  <div style={{
                    marginTop: '4px',
                    display: 'inline-block',
                    padding: '2px 8px',
                    borderRadius: '4px',
                    fontSize: '0.6rem',
                    fontWeight: 700,
                    background: prices[lid].status === 'LOW' ? '#fef2f2' : '#fffbeb',
                    border: `1px solid ${prices[lid].status === 'LOW' ? '#fecaca' : '#fde68a'}`,
                    color: prices[lid].status === 'LOW' ? '#dc2626' : '#d97706',
                  }}>
                    {prices[lid].status === 'LOW' ? '↓ LOW' : '↑ HIGH'} vs ${prices[lid].avg.toFixed(3)}
                  </div>
                )}
                

                {isFlagged ? (
                  <CorrectionPanel
                    lot={lot}
                    onUnflag={() => handleUnflag(lid)}
                    onPushed={handlePushed}
                  />
                ) : (
                  <>
                  <div style={{ display: 'flex', gap: '4px', marginTop: '6px' }}>
                    <button
                      onClick={() => handleFound(lid)}
                      style={{
                        flex: 1,
                        padding: '7px 4px',
                        background: '#f0fdf4',
                        border: '1px solid #bbf7d0',
                        borderRadius: '6px',
                        color: colors.greenDark,
                        fontFamily: "'Outfit', sans-serif",
                        fontWeight: 600,
                        fontSize: '0.72rem',
                        cursor: 'pointer',
                      }}
                    >
                      ✓ Found
                    </button>
                    <button
                      onClick={() => { setMoving(moving === lid ? null : lid); setMoveBin(''); }}
                      style={{
                        flex: 1,
                        padding: '7px 4px',
                        background: moving === lid ? '#dbeafe' : '#eff6ff',
                        border: `1px solid ${moving === lid ? '#93c5fd' : '#bfdbfe'}`,
                        borderRadius: '6px',
                        color: '#2563eb',
                        fontFamily: "'Outfit', sans-serif",
                        fontWeight: 600,
                        fontSize: '0.72rem',
                        cursor: 'pointer',
                      }}
                    >
                      ↔ Move
                    </button>
                    <button
                      onClick={() => handleFlag(lid)}
                      style={{
                        flex: 1,
                        padding: '7px 4px',
                        background: '#fff7ed',
                        border: '1px solid #fed7aa',
                        borderRadius: '6px',
                        color: '#ea580c',
                        fontFamily: "'Outfit', sans-serif",
                        fontWeight: 600,
                        fontSize: '0.72rem',
                        cursor: 'pointer',
                      }}
                    >
                      ⚑ Fix
                    </button>
                    <button
                      onClick={() => handleSkip(lid)}
                      style={{
                        flex: 1,
                        padding: '7px 4px',
                        background: '#f5f3ff',
                        border: '1px solid #ddd6fe',
                        borderRadius: '6px',
                        color: '#7c3aed',
                        fontFamily: "'Outfit', sans-serif",
                        fontWeight: 600,
                        fontSize: '0.72rem',
                        cursor: 'pointer',
                      }}
                    >
                      → Skip
                    </button>
                  </div>
                  {moving === lid && (
                    <div style={{
                      marginTop: '6px', padding: '10px',
                      background: '#eff6ff', border: '1px solid #bfdbfe',
                      borderLeft: '3px solid #2563eb', borderRadius: '6px',
                    }}>
                      <div style={{ fontSize: '0.68rem', fontWeight: 700, color: '#2563eb', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '6px' }}>Move to bin</div>
                      <div style={{ display: 'flex', gap: '6px' }}>
                        <input
                          type="text"
                          value={moveBin}
                          onChange={e => setMoveBin(e.target.value)}
                          placeholder="e.g. AB015"
                          onKeyDown={e => { if (e.key === 'Enter') handleMove(lid); }}
                          style={{
                            flex: 1, padding: '6px 8px', background: '#fff',
                            border: `1px solid ${colors.border}`, borderRadius: '4px',
                            fontFamily: "'Outfit', sans-serif", fontSize: '0.85rem',
                            fontWeight: 700, color: colors.textPrimary, outline: 'none',
                            textAlign: 'center',
                          }}
                        />
                        <button
                          onClick={() => handleMove(lid)}
                          disabled={movePushing || !moveBin.trim()}
                          style={{
                            padding: '6px 14px',
                            background: moveBin.trim() ? '#2563eb' : '#d1d5db',
                            color: '#fff', border: 'none', borderRadius: '4px',
                            fontFamily: "'Outfit', sans-serif", fontWeight: 700,
                            fontSize: '0.75rem', cursor: moveBin.trim() ? 'pointer' : 'not-allowed',
                          }}
                        >{movePushing ? '...' : 'Move'}</button>
                      </div>
                    </div>
                  )}
                  </>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Bottom controls */}
      <div style={{ display: 'flex', gap: '10px', marginTop: '24px' }}>
        <button
          onClick={() => { setBinIndex(prev => prev + 1); setSearch(''); }}
          style={{
            flex: 1,
            padding: '12px',
            background: colors.cardBg,
            border: `1px solid ${colors.border}`,
            borderRadius: '6px',
            color: colors.textSecondary,
            fontFamily: "'Outfit', sans-serif",
            fontWeight: 600,
            fontSize: '0.85rem',
            cursor: 'pointer',
          }}
        >
          Skip to Next Bin
        </button>
        <button
          onClick={() => setAuditMode(false)}
          style={{
            flex: 1,
            padding: '12px',
            background: colors.navy,
            color: '#ffffff',
            border: 'none',
            borderRadius: '6px',
            fontFamily: "'Outfit', sans-serif",
            fontWeight: 700,
            fontSize: '0.85rem',
            cursor: 'pointer',
          }}
        >
          Exit Audit Mode
        </button>
      </div>
    </div>
  );
}

export default Audit;